import React from 'react';

import { User } from '@/models/User';
import { Document } from '@react-pdf/renderer';
import DocumentPageLayout from '../../components/DocumentPageLayout';
import DocumentHeadingDefault from '../DocumentHeadingDefault';
import { documentPdf, PrintOptions } from '../../helpers/print';

export interface ClinicalDocumentBaseProps {
  photo?: string;
  encounter?: fhir4.Encounter | null;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

export interface ClinicalDocumentProps<T extends fhir4.DomainResource>
  extends ClinicalDocumentBaseProps {
  resource: T;
}

type Props = ClinicalDocumentBaseProps &
  React.PropsWithChildren & {
    title: string;
    wrap?: boolean;
  };

const ClinicalDocumentLayout: React.FC<Props> = ({
  title,
  photo,
  encounter,
  footer,
  header,
  children,
  wrap = true,
}) => {
  const Header = React.useMemo(() => {
    if (header) {
      return header;
    }

    if (encounter) {
      return (
        <DocumentHeadingDefault
          encounter={encounter}
          title={title}
          photo={photo}
        />
      );
    }
    return null;
  }, [encounter, title, photo, header]);

  return (
    <DocumentPageLayout wrap={wrap} header={Header} footer={footer}>
      {children}
    </DocumentPageLayout>
  );
};

interface PrintClinicalDocumentOptions<T extends fhir4.DomainResource> {
  user?: User | null;
  encounter?: fhir4.Encounter | null;
  resource: T;
  options?: PrintOptions;
}

export function clinicalDocumentPdf<T extends fhir4.DomainResource>(
  ClinicalDocument: React.ComponentType<ClinicalDocumentProps<T>>,
  {
    user,
    resource,
    encounter,
    options = { popup: true, printDialog: true },
  }: PrintClinicalDocumentOptions<T>,
): Promise<void> {
  let photo = '';
  if (user && user.empresa) {
    photo = user.empresa.logo || '';
  }

  return documentPdf(
    <Document>
      <ClinicalDocument
        resource={resource}
        encounter={encounter}
        photo={photo}
      />
    </Document>,
    options,
  );
}

export default ClinicalDocumentLayout;
